import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  fetchCustomers,
  fetchCustomerById,
  createCustomer,
  updateCustomer,
  searchCustomers,
  fetchCustomerProjects,
} from '../actions/customerActions';
import { CustomerState, CustomerData } from '../types/customerTypes';

const initialState: CustomerState = {
  customers: [],
  currentCustomer: null,
  loading: false,
  error: null,
  pagination: null,
  searchResults: [],
  searching: false,
  selectedCustomerProjects: [],
  projectsLoading: false,
  projectsPagination: null,
};

const customerSlice = createSlice({
  name: 'customers',
  initialState,
  reducers: {
    clearCustomerError: state => {
      state.error = null;
    },
    clearCurrentCustomer: state => {
      state.currentCustomer = null;
    },
    clearSearchResults: state => {
      state.searchResults = [];
      state.searching = false;
    },
    invalidateCustomersCache: state => {
      state.customers = [];
      state.pagination = null;
    },
    clearCustomerProjects: state => {
      state.selectedCustomerProjects = [];
      state.projectsPagination = null;
    },
  },
  extraReducers: builder => {
    // Handle fetchCustomers
    builder
      .addCase(fetchCustomers.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomers.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        const newCustomers = action.payload.data || [];
        const newPagination = action.payload.pagination || null;
        
        // If it's page 1, replace the customers array (initial load or refresh)
        // Otherwise, append new customers (lazy loading)
        if (newPagination && newPagination.currentPage === 1) {
          state.customers = newCustomers;
        } else {
          // Append new customers, avoiding duplicates
          const existingIds = new Set(state.customers.map((c: any) => c.id));
          const uniqueNewCustomers = newCustomers.filter((c: any) => !existingIds.has(c.id));
          state.customers = [
            ...state.customers,
            ...uniqueNewCustomers,
          ];
        }
        
        state.pagination = newPagination;
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || 'Failed to fetch customers';
      });

    // Handle fetchCustomerById
    builder
      .addCase(fetchCustomerById.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomerById.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.currentCustomer = action.payload.data || null;
      })
      .addCase(fetchCustomerById.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || 'Failed to fetch customer';
      });

    // Handle createCustomer
    builder
      .addCase(createCustomer.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCustomer.fulfilled, state => {
        state.loading = false;
        // We don't need to update state here as we'll reload the list
      })
      .addCase(createCustomer.rejected, (state, action) => {
        state.loading = false;
        // Store the complete error object or just the message string
        state.error = (action.payload as string) || 'Failed to create customer';
      });

    // Handle updateCustomer
    builder
      .addCase(updateCustomer.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCustomer.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        if (state.currentCustomer && action.payload.data) {
          state.currentCustomer = action.payload.data;
        }
      })
      .addCase(updateCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || 'Failed to update customer';
      });

    // Handle searchCustomers
    builder
      .addCase(searchCustomers.pending, state => {
        state.searching = true;
        state.error = null;
      })
      .addCase(searchCustomers.fulfilled, (state, action: PayloadAction<any>) => {
        state.searching = false;
        state.searchResults = action.payload.data || [];
      })
      .addCase(searchCustomers.rejected, (state, action) => {
        state.searching = false;
        state.error = (action.payload as string) || 'Failed to search customers';
      });

    // Handle fetchCustomerProjects
    builder
      .addCase(fetchCustomerProjects.pending, state => {
        state.projectsLoading = true;
        state.error = null;
      })
      .addCase(fetchCustomerProjects.fulfilled, (state, action: PayloadAction<any>) => {
        state.projectsLoading = false;
        const newProjects = action.payload.data || [];
        const newPagination = action.payload.pagination || null;

        // If it's page 1, replace the projects array (initial load or refresh)
        // Otherwise, append new projects (lazy loading)
        if (newPagination && newPagination.page === 1) {
          state.selectedCustomerProjects = newProjects;
        } else {
          // Append new projects, avoiding duplicates
          const existingIds = new Set(state.selectedCustomerProjects.map((p: any) => p.id));
          const uniqueNewProjects = newProjects.filter((p: any) => !existingIds.has(p.id));
          state.selectedCustomerProjects = [
            ...state.selectedCustomerProjects,
            ...uniqueNewProjects,
          ];
        }

        state.projectsPagination = newPagination;
      })
      .addCase(fetchCustomerProjects.rejected, (state, action) => {
        state.projectsLoading = false;
        state.error = (action.payload as string) || 'Failed to fetch customer projects';
      });
  },
});

export const {
  clearCustomerError,
  clearCurrentCustomer,
  clearSearchResults,
  invalidateCustomersCache,
  clearCustomerProjects,
} = customerSlice.actions;

export default customerSlice.reducer;
